import React, { useEffect, useState } from 'react';

// 定义布局常量
const LAYOUT = {
  desktop: {
    cardHeight: 32,    // 扑克牌高度
    cardWidth: 24,     // 扑克牌宽度
    titleSize: 24,     // 标题字体大小
    subtitleSize: 18,  // 副标题字体大小
    streetHeaderSize: 18, // 街道标题(PREFLOP等)字体大小
    actionSize: 16,    // 动作文本字体大小
    actionHeight: 36,  // 每个动作项的高度
    padding: 20,       // 整体内边距
    gap: 8,           // 元素间距
  },
  mobile: {
    cardHeight: 24,
    cardWidth: 18,
    titleSize: 20,
    subtitleSize: 16,
    streetHeaderSize: 16,
    actionSize: 14,
    actionHeight: 40,
    padding: 16,
    gap: 6,
  }
};

// 组件的 props 类型定义（使用 PropTypes 而不是 TypeScript）
const HandHistoryCard = ({ 
  actions, 
  gameInfo, 
  heroInfo = { position: '', hand: [] }, // 提供默认值，使用hand而不是cards
  boardCards,
  isMobile = false,
  customStyles = {} // 添加自定义样式参数
}) => {
  const layout = isMobile ? LAYOUT.mobile : LAYOUT.desktop;
  
  // 定义固定的样式类
  const styleclasses = {
    background: 'bg-gray-900 text-white',
    actionBg: 'bg-gray-800',
    streetHeader: 'bg-blue-900'
  };

  const renderCard = (card) => {
    if (!card) return null;
    
    return (
      <div
        className="inline-block rounded-sm shadow-sm"
        style={{
          width: layout.cardWidth,
          height: layout.cardHeight,
          backgroundImage: `url(/images/cards/${card}_borderless.svg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          margin: 0,
          padding: 0,
          ...customStyles.card
        }}
      />
    );
  };

  // 添加渲染公共牌的函数
  const renderBoardCards = (street) => {
    if (!boardCards || !boardCards[street] || boardCards[street].length === 0) {
      return null;
    }
    
    return (
      <div className="flex ml-2 space-x-1" style={customStyles.boardCards}>
        {boardCards[street].map((card, index) => (
          <div key={`board-${street}-${index}`} className="inline-block">
            {renderCard(card)}
          </div>
        ))}
      </div>
    );
  };

  const mergeActions = (streetActions) => {
    if (!streetActions || streetActions.length === 0) return [];
    
    const merged = [];
    let currentGroup = {
      players: [streetActions[0].player],
      action: streetActions[0].action,
      amount: streetActions[0].amount
    };

    for (let i = 1; i < streetActions.length; i++) {
      const current = streetActions[i];
      if (current.action === currentGroup.action && 
          current.amount === currentGroup.amount) {
        currentGroup.players.push(current.player);
      } else {
        merged.push(currentGroup);
        currentGroup = {
          players: [current.player],
          action: current.action,
          amount: current.amount
        };
      }
    }
    merged.push(currentGroup);
    return merged;
  };

  const formatPlayerNames = (players, maxLength = 20) => {
    if (!players || players.length === 0) return '';
    
    // 如果总长度在限制范围内，直接返回完整列表
    const fullNames = players.join(', ');
    if (fullNames.length <= maxLength) {
      return fullNames;
    }
    
    // 否则使用缩略格式：第一个玩家...最后一个玩家
    if (players.length > 2) {
      return `${players[0]}...${players[players.length - 1]}`;
    }
    
    return fullNames; // 如果只有两个玩家，仍然显示完整
  };

  // 定义统一的样式
  const actionStyle = {
    padding: `${layout.gap}px`,
    fontSize: layout.actionSize,
    lineHeight: `${layout.actionSize * 1.2}px`,
    marginBottom: layout.gap / 2,
    ...customStyles.action
  };

  const renderAction = (action) => {
    if (!action) return null;

    // 获取格式化后的玩家名称
    const playerNames = formatPlayerNames(action.players);
    
    // 根据文本长度计算字体大小
    let fontSize = layout.actionSize;
    if (playerNames.length > 12) {
      fontSize = Math.max(
        (layout.actionSize * 12) / playerNames.length,
        layout.actionSize * 0.75 // 最小字体大小为原始大小的 75%
      );
    }

    return (
      <div 
        className={`${customStyles.actionClassName || styleclasses.actionBg} rounded-lg`}
        style={actionStyle}
      >
        <div className="flex justify-between">
          <span style={customStyles.actionText}>
            <strong>{playerNames}</strong> {action.action}
            {(action.action === 'bet' || action.action === 'raise' || action.action === 'call') && 
              ` ${action.amount}`}
          </span>
          {(action.action === 'bet' || action.action === 'raise' || action.action === 'call') && (
            <span className="opacity-80" style={customStyles.potText}>Pot: {action.potSize}</span>
          )}
        </div>
      </div>
    );
  };

  const renderHeroHand = () => {
    if (!heroInfo?.hand || heroInfo.hand.length !== 2) return null;

    return (
      <div className="mb-4" style={customStyles.heroHand}>
        <div className="flex items-center">
          <span className="mr-2" style={customStyles.heroPosition}>Hero ({heroInfo.position}):</span>
          <div className="flex space-x-1">
            {heroInfo.hand.map((card, index) => (
              <div key={`hero-card-${index}`} className="inline-block">
                {renderCard(card)}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const containerStyle = {
    padding: layout.padding,
    fontSize: layout.actionSize,
    lineHeight: `${layout.actionSize * 1.5}px`,
    ...customStyles.container
  };

  const streetHeaderStyle = {
    fontSize: layout.streetHeaderSize,
    padding: `${layout.gap}px`,
    marginBottom: layout.gap,
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    ...customStyles.street
  };

  return (
    <div 
      className={`${customStyles.bgClassName || styleclasses.background} rounded-xl`}
      style={containerStyle}
    >
      {/* 标题部分 */}
      <div className="mb-4" style={customStyles.header}>
        <h2 style={{ 
          fontSize: layout.titleSize, 
          fontWeight: 'bold', 
          marginBottom: layout.gap / 2,
          ...customStyles.title
        }}>
          {gameInfo.stakes} {gameInfo.gameType}
        </h2>
        <h3 style={{ 
          fontSize: layout.subtitleSize, 
          opacity: 0.9,
          ...customStyles.subtitle
        }}>
          {gameInfo.players} handed
        </h3>
      </div>

      {/* 玩家手牌部分 */}
      {renderHeroHand()}

      {/* 街道动作部分 */}
      {Object.entries(actions).map(([street, streetActions]) => {
        if (streetActions.length === 0) return null;
        
        return (
          <div key={street} className="mb-4">
            <div 
              className={`${customStyles.streetClassName || styleclasses.streetHeader} rounded-lg`}
              style={streetHeaderStyle}
            >
              {street.charAt(0).toUpperCase() + street.slice(1)}
              {street !== 'preflop' && renderBoardCards(street)}
            </div>
            
            <div className="mt-2">
              {streetActions.map((action, index) => (
                <div 
                  key={`${street}-action-${index}`}
                  className={`${customStyles.actionClassName || styleclasses.actionBg} rounded-lg`}
                  style={actionStyle}
                >
                  <div className="flex justify-between">
                    <span style={customStyles.actionText}>
                      <strong>{action.player}</strong> {action.action}
                      {(action.action === 'bet' || action.action === 'raise' || action.action === 'call') && 
                        ` ${action.amount}`}
                    </span>
                    {(action.action === 'bet' || action.action === 'raise' || action.action === 'call') && (
                      <span className="opacity-80" style={customStyles.potText}>Pot: {action.potSize}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default HandHistoryCard; 